import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, FormControl, IconButton, Tooltip } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import {
    thirdPartyTagFormDefaultValues,
    ThirdPartyTagRuleForm,
    thirdPartyTagSchema,
} from "validation/components/ThirdPartyTag/thirdPartyTagRules";
import { SecondaryButton, TemplateViewer, TextField } from "components";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchThirdPartyTag,
    submitThirdPartyTag,
    getMaterialDetailsById,
    submitNewCreativeThirdPartyTag,
    submitPrintMaterialDescription,
    getThirdPartyStatus,
    submitPrintMaterialAdServerProperties,
    getConfig,
} from "store/features/materialSpecs/specs";
import { FormLine, ToggleButton } from "./ThirdPartyTagForm.styled";
import { useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { LoadingOverlay } from "components/LoadingOverlay/LoadingOverlay";
import { usePermissions, usePreviousProp } from "hooks";
import { isEmpty } from "utils";
import { ToggleButtonGroup } from "@material-ui/lab";

const CopyToClipboard = ({ value }: { value: string }) => {
    const { t } = useTranslation();

    return (
        <Tooltip title={t("campaign.thirdPartyTag.label.copyToClipboard") || ""} interactive placement="top">
            <IconButton onClick={() => navigator.clipboard.writeText(value)}>
                <FileCopyIcon />
            </IconButton>
        </Tooltip>
    );
};

export const ThirdPartyTagForm = ({
    disableAccordion = false,
    customerSummary = false,
    enableOnBlurSubmit = false,
    isMaterialEmpty = false,
    enableOverlay = true,
    isOnCustomerTab = false,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [tagTemplate, setTagTemplate] = useState<string>("");
    const {
        selectedMaterialSpecId,
        selectedMaterialId,
        isOptionalSelected,
        isPrinted,
        customerNote,
        setTab,
    } = useMaterialSpecs();
    const materialDetails = useSelector(getMaterialDetailsById(selectedMaterialSpecId, selectedMaterialId));
    const isThirdPartyMaterial = !isPrinted && (materialDetails?.isThirdPartyTagMaterial || isMaterialEmpty);
    const showSgCode = useSelector(getConfig("showSgCode"));
    const [selectedType, setSelectedType] = useState<"3party" | "sgCode">("3party");
    const { handleSubmit, control, setValue, errors, reset, formState } = useForm<ThirdPartyTagRuleForm>({
        resolver: yupResolver(thirdPartyTagSchema(isThirdPartyMaterial)),
        defaultValues: thirdPartyTagFormDefaultValues,
    });
    const { UPLOAD_ADMATERIAL } = usePermissions(materialDetails?.permissions);
    const isThirdPartyProcessing = useSelector(getThirdPartyStatus);
    const prevThirdPartyProcessingStatus = usePreviousProp(isThirdPartyProcessing);
    const materialId = materialDetails?.id;
    const disabledPopupMessage = t("campaign.thirdPartyTag.label.disabledPopup") || "";
    const isEmptyPrintMaterial = isPrinted && selectedMaterialId !== undefined && selectedMaterialId < 0;

    const onSubmit = (values: ThirdPartyTagRuleForm) => {
        // standard case for digitals
        if (!isPrinted && selectedMaterialSpecId && selectedMaterialId) {
            const sgCodeNum = parseInt(values.sgCode);

            // set undefined values to request if no changes were made
            dispatch(
                submitThirdPartyTag.request({
                    materialSpecificationId: selectedMaterialSpecId,
                    adMaterialId: selectedMaterialId,
                    tag: values.tag === materialDetails?.tag ? undefined : values.tag,
                    sgCode: !isNaN(sgCodeNum)
                        ? sgCodeNum === materialDetails?.sgCode
                            ? undefined
                            : sgCodeNum
                        : undefined,
                    pixelTracker:
                        values.pixelTracker === materialDetails?.pixelTracker ? undefined : values.pixelTracker,
                    description: values.description === materialDetails?.description ? undefined : values.description,
                    url: values.url === materialDetails?.url ? undefined : values.url,
                })
            );
            // optional (available only in digitals) creative case
        } else if (selectedMaterialSpecId && isOptionalSelected) {
            dispatch(
                submitNewCreativeThirdPartyTag.request({
                    ...values,
                    materialSpecificationId: selectedMaterialSpecId,
                })
            );
            // prints
        } else if (isPrinted && selectedMaterialSpecId !== undefined && selectedMaterialId !== undefined) {
            if (selectedMaterialId < 0) {
                dispatch(
                    submitPrintMaterialDescription.request({
                        materialSpecificationId: selectedMaterialSpecId,
                        description: values.description,
                    })
                );
            }
            // for prints with added material
            dispatch(
                submitPrintMaterialAdServerProperties.request({
                    materialSpecificationId: selectedMaterialSpecId,
                    adMaterialId: selectedMaterialId,
                    description: values.description === customerNote ? undefined : values.description,
                    url: values.url,
                })
            );
        }

        setTab("CUSTOMER_SUMMARY");
    };

    const handleOnBlurSubmit = (e: React.FocusEvent<HTMLInputElement>) => {
        // update only when enableOnBlurSubmit is enabled and fields were updated
        if (enableOnBlurSubmit && !isEmpty(formState.dirtyFields)) {
            handleSubmit((values) => onSubmit(values))(e);
        }
    };

    useEffect(() => {
        if (materialDetails) {
            if (!materialDetails.tag && selectedMaterialSpecId && selectedMaterialId && materialDetails.id > 0) {
                dispatch(
                    fetchThirdPartyTag.request({
                        currentMaterialSpecificationId: selectedMaterialSpecId,
                        adMaterialId: selectedMaterialId,
                    })
                );

                return;
            }
        }

        if (isOptionalSelected) {
            reset(thirdPartyTagFormDefaultValues);
            setTagTemplate("");
        }
        //eslint-disable-next-line
    }, [materialId]);

    useEffect(() => {
        if (materialDetails) {
            const { sgCode, pixelTracker, url, description, tag } = materialDetails;
            setTagTemplate(materialDetails.tag || "");
            // TODO: check sgCode, sometimes comes as undefined
            reset({
                sgCode: sgCode?.toString() || "",
                pixelTracker,
                url,
                tag,
                description: isPrinted ? customerNote : description,
            });
        }
    }, [
        materialDetails, // TODO: get rid of this object from deps, PERFORMANCE ISSUE!
        setValue,
        reset,
        selectedMaterialId,
        isPrinted,
        isThirdPartyProcessing,
        prevThirdPartyProcessingStatus,
        customerNote,
    ]);

    return (
        <LoadingOverlay active={isThirdPartyProcessing} visible={enableOverlay}>
            {!isOnCustomerTab && showSgCode && (
                <Box display="flex" ml={1}>
                    <ToggleButtonGroup
                        value={selectedType}
                        exclusive
                        onChange={(e, type) => {
                            // avoid situation where none of the options is selected
                            if (type) {
                                setSelectedType(type);
                            }
                        }}
                        size="small">
                        <ToggleButton value="3party" disableRipple>
                            {t("campaign.thirdPartyTag.label.thirdParty")}
                        </ToggleButton>
                        <ToggleButton value="sgCode" disableRipple>
                            {t("campaign.thirdPartyTag.label.sgCode")}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1 }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    {(isThirdPartyMaterial || isOptionalSelected || isMaterialEmpty) && (
                        <>
                            {selectedType === "3party" ? (
                                <FormLine>
                                    <FormControl fullWidth={true}>
                                        <Controller
                                            render={() => (
                                                <TemplateViewer
                                                    disableAccordion={disableAccordion}
                                                    lineWrapping
                                                    label={t("campaign.thirdPartyTag.label.thirdParty")}
                                                    template={tagTemplate}
                                                    showTemplatePreview={false}
                                                    templatePreview=""
                                                    onSetTemplate={(body) => {
                                                        setTagTemplate(body);
                                                        setValue("tag", body);
                                                    }}
                                                    readOnly={customerSummary}
                                                />
                                            )}
                                            name="tag"
                                            control={control}
                                            defaultValue=""
                                        />
                                    </FormControl>
                                </FormLine>
                            ) : (
                                <FormLine>
                                    <FormControl fullWidth={true}>
                                        <Controller
                                            render={({ onChange, value }) => (
                                                <TextField
                                                    variant="outlined"
                                                    backgroundColor="white"
                                                    value={value}
                                                    type="number"
                                                    label={t("campaign.thirdPartyTag.label.sgCode")}
                                                    onChange={(e) => onChange(e.target.value)}
                                                />
                                            )}
                                            name="sgCode"
                                            control={control}
                                            defaultValue=""
                                        />
                                    </FormControl>
                                </FormLine>
                            )}
                        </>
                    )}
                    <FormLine>
                        <Tooltip title={isOptionalSelected ? disabledPopupMessage : ""}>
                            <FormControl fullWidth={true}>
                                <Controller
                                    render={({ onChange, value }) => (
                                        <TextField
                                            disabled={!UPLOAD_ADMATERIAL?.display}
                                            variant="outlined"
                                            backgroundColor="white"
                                            value={value}
                                            multiline
                                            rows={3}
                                            label={t("campaign.thirdPartyTag.label.description")}
                                            onChange={(e) => onChange(e.target.value)}
                                            onBlur={handleOnBlurSubmit}
                                        />
                                    )}
                                    name="description"
                                    control={control}
                                    defaultValue=""
                                    multiline
                                />
                            </FormControl>
                        </Tooltip>
                    </FormLine>
                    {!isPrinted && (
                        <FormLine>
                            <Tooltip title={isOptionalSelected ? disabledPopupMessage : ""}>
                                <FormControl fullWidth={true}>
                                    <Controller
                                        render={({ onChange, value }) => (
                                            <TextField
                                                disabled={!UPLOAD_ADMATERIAL?.display}
                                                variant="outlined"
                                                backgroundColor="white"
                                                value={value}
                                                label={t("campaign.thirdPartyTag.label.pixelTracker")}
                                                onChange={(e) => onChange(e.target.value)}
                                                onBlur={handleOnBlurSubmit}
                                                InputProps={{
                                                    endAdornment: <CopyToClipboard value={value} />,
                                                }}
                                            />
                                        )}
                                        name="pixelTracker"
                                        control={control}
                                        defaultValue=""
                                    />
                                </FormControl>
                            </Tooltip>
                        </FormLine>
                    )}
                    {((customerSummary && !isThirdPartyMaterial) || selectedType === "sgCode") && (
                        <FormLine>
                            <Tooltip
                                title={
                                    isEmptyPrintMaterial || (isOptionalSelected && selectedType === "sgCode")
                                        ? disabledPopupMessage
                                        : ""
                                }>
                                <FormControl fullWidth={true}>
                                    <Controller
                                        render={({ onChange, value }) => (
                                            <TextField
                                                disabled={!UPLOAD_ADMATERIAL?.display || isEmptyPrintMaterial}
                                                variant="outlined"
                                                backgroundColor="white"
                                                error={!!errors.url}
                                                errorMessage={errors.url ? t("campaign.errors.invalidUrl") : undefined}
                                                value={value}
                                                label={t("campaign.thirdPartyTag.label.url")}
                                                onChange={(e) => onChange(e.target.value)}
                                                onBlur={handleOnBlurSubmit}
                                                InputProps={{
                                                    endAdornment: <CopyToClipboard value={value} />,
                                                }}
                                            />
                                        )}
                                        name="url"
                                        control={control}
                                        defaultValue={materialDetails?.url || ""}
                                    />
                                </FormControl>
                            </Tooltip>
                        </FormLine>
                    )}

                    {(UPLOAD_ADMATERIAL?.display || isOptionalSelected) && !enableOnBlurSubmit && (
                        <FormLine>
                            <Box display="flex" justifyContent="flex-end">
                                <Tooltip title={UPLOAD_ADMATERIAL?.tooltip || ""}>
                                    <span>
                                        <SecondaryButton
                                            disabled={isThirdPartyProcessing}
                                            disableRipple
                                            type="submit"
                                            color="primary"
                                            variant="outlined">
                                            {t("buttons.label.submit")}
                                        </SecondaryButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </FormLine>
                    )}
                </Box>
            </form>
        </LoadingOverlay>
    );
};
